/**
 * 神獸作坊：近看換件、捏黏土。配方只存數字，不存 mesh。
 */

import { useGameStore } from '../core/store/gameStore'
import {
  ANIMAL_SOURCES,
  CLAY_TINTS,
  ENCOUNTER_LABEL,
  TEMPERAMENT_LABEL,
  defaultBeastForm,
} from '../content/partLibrary'
import type { AnimalSlot, BeastForm, EncounterKind, Temperament } from '../content/types'

const SLOTS: { slot: AnimalSlot; label: string; key: 'head' | 'body' | 'tail' }[] = [
  { slot: 'head', label: '面', key: 'head' },
  { slot: 'body', label: '身', key: 'body' },
  { slot: 'tail', label: '尾', key: 'tail' },
]

const CLAY_SLIDERS: { key: keyof BeastForm; label: string; min: number; max: number; step: number }[] = [
  { key: 'headScale', label: '頭', min: 0.7, max: 1.55, step: 0.02 },
  { key: 'headStretch', label: '顱寬', min: 0.75, max: 1.4, step: 0.02 },
  { key: 'snout', label: '吻', min: 0.65, max: 1.55, step: 0.02 },
  { key: 'earSpread', label: '耳距', min: 0.55, max: 1.55, step: 0.02 },
  { key: 'bodyScale', label: '身粗', min: 0.7, max: 1.4, step: 0.02 },
  { key: 'bodyLength', label: '身長', min: 0.7, max: 1.5, step: 0.02 },
  { key: 'tailScale', label: '尾', min: 0.45, max: 1.8, step: 0.02 },
  { key: 'tailCurl', label: '尾捲', min: -0.6, max: 1.1, step: 0.02 },
]

export function BeastStudio() {
  const draft = useGameStore(s => s.draft)
  const setDraft = useGameStore(s => s.setDraft)
  const setDraftPart = useGameStore(s => s.setDraftPart)
  const setMakerOpen = useGameStore(s => s.setMakerOpen)
  const setPlacingBeast = useGameStore(s => s.setPlacingBeast)
  const setToolGroup = useGameStore(s => s.setToolGroup)
  const visitMode = useGameStore(s => s.visitMode)
  const studioWalk = useGameStore(s => s.studioWalk)
  const setStudioWalk = useGameStore(s => s.setStudioWalk)
  const clay = { ...defaultBeastForm(), ...draft.beastForm }

  if (visitMode) return null

  const sourceOf = (slot: AnimalSlot) => draft.parts.find(p => p.slot === slot)?.source ?? 'yang'

  const pinch = (key: keyof BeastForm, value: number) => {
    setDraft({ beastForm: { ...clay, [key]: value } })
  }

  return (
    <div className="beast-studio">
      <aside className="studio-rail studio-rail-left">
        <div className="studio-rail-head">
          <span className="studio-title">神獸作坊</span>
          <button type="button" className="panel-close" onClick={() => setMakerOpen(false)}>×</button>
        </div>
        <label className="beast-field">
          名
          <input
            value={draft.name}
            onChange={e => setDraft({ name: e.target.value })}
            maxLength={12}
          />
        </label>
        {SLOTS.map(({ slot, label, key }) => (
          <div key={slot} className="studio-slot">
            <span className="studio-slot-label">{label}</span>
            <div className="studio-chips">
              {ANIMAL_SOURCES.map(src => (
                <button
                  key={src.id}
                  type="button"
                  className={sourceOf(slot) === src.id ? 'active' : ''}
                  onClick={() => setDraftPart(slot, src.id)}
                >
                  <i className="studio-blob" style={{ background: src[key] }} />
                  <b>{src.name}</b>
                </button>
              ))}
            </div>
          </div>
        ))}
        <p className="beast-hint">換件立刻見於石上。右鍵可轉、滾輪走近。</p>
      </aside>

      <aside className="studio-rail studio-rail-right">
        <h3>捏</h3>
        <p className="beast-hint">像捏黏土。只拉比例，不改骨架。</p>
        {CLAY_SLIDERS.map(s => (
          <label key={s.key} className="studio-clay">
            <span>{s.label}</span>
            <input
              type="range"
              min={s.min}
              max={s.max}
              step={s.step}
              value={Number(clay[s.key]) || 0}
              onChange={e => pinch(s.key, Number(e.target.value))}
            />
          </label>
        ))}
        <div className="studio-slot">
          <span className="studio-slot-label">釉</span>
          <div className="studio-chips">
            {CLAY_TINTS.map(t => (
              <button
                key={t.id}
                type="button"
                className={(clay.tint ?? null) === t.hex ? 'active' : ''}
                onClick={() => setDraft({ beastForm: { ...clay, tint: t.hex } })}
              >
                <i className="studio-blob" style={{ background: t.hex ?? '#c4b49a' }} />
                <b>{t.name}</b>
              </button>
            ))}
          </div>
        </div>
        <h3>性</h3>
        <div className="studio-choices">
          {(Object.keys(TEMPERAMENT_LABEL) as Temperament[]).map(k => (
            <button
              key={k}
              type="button"
              className={`beast-choice ${draft.temperament === k ? 'active' : ''}`}
              onClick={() => setDraft({ temperament: k })}
            >
              {TEMPERAMENT_LABEL[k]}
            </button>
          ))}
        </div>
        <h3>遇</h3>
        <div className="studio-choices">
          {(Object.keys(ENCOUNTER_LABEL) as EncounterKind[]).map(k => (
            <button
              key={k}
              type="button"
              className={`beast-choice ${draft.encounter === k ? 'active' : ''}`}
              onClick={() => setDraft({ encounter: k })}
            >
              {ENCOUNTER_LABEL[k]}
            </button>
          ))}
        </div>
        {draft.encounter === 'message' && (
          <label className="beast-field">
            短訊
            <input
              value={draft.encounterMessage ?? ''}
              onChange={e => setDraft({ encounterMessage: e.target.value })}
              maxLength={24}
            />
          </label>
        )}
      </aside>

      <div className="studio-foot">
        <button
          type="button"
          className={`beast-choice ${!studioWalk ? 'active' : ''}`}
          onClick={() => setStudioWalk(false)}
        >
          立
        </button>
        <button
          type="button"
          className={`beast-choice ${studioWalk ? 'active' : ''}`}
          onClick={() => setStudioWalk(true)}
        >
          走
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            setToolGroup('beast')
            setPlacingBeast(true)
            setMakerOpen(false)
          }}
        >
          投放山上
        </button>
      </div>
    </div>
  )
}
